import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"

import { FalhaNaSecao } from "../componentes/Secao"
import { Skeleton } from "../componentes/Skeleton"

export type Pedido<T> = {
  dados: T | null
  erro: string | null
  carregando: boolean
  recarregar: () => void
  definir: (dados: T) => void
}

function causaDa(falha: unknown): string {
  return falha instanceof Error && falha.message !== "" ? falha.message : "falha ao carregar"
}

export function usePedido<T>(buscar: () => Promise<T>, chave: string, ativa = true): Pedido<T> {
  const [estado, definirEstado] = useState<{ dados: T | null; erro: string | null; carregando: boolean }>({
    dados: null,
    erro: null,
    carregando: ativa,
  })
  const [tentativa, definirTentativa] = useState(0)
  const buscarAtual = useRef(buscar)
  buscarAtual.current = buscar

  useEffect(() => {
    if (!ativa) return
    let vivo = true
    definirEstado({ dados: null, carregando: true, erro: null })
    buscarAtual
      .current()
      .then((dados) => {
        if (vivo) definirEstado({ dados, erro: null, carregando: false })
      })
      .catch((falha: unknown) => {
        if (vivo) definirEstado({ dados: null, erro: causaDa(falha), carregando: false })
      })
    return () => {
      vivo = false
    }
  }, [chave, tentativa, ativa])

  const recarregar = useCallback(() => definirTentativa((n) => n + 1), [])
  const definir = useCallback((dados: T) => definirEstado({ dados, erro: null, carregando: false }), [])
  return { ...estado, recarregar, definir }
}

export function EsqueletoDeTabela({ linhas = 3 }: { linhas?: number }) {
  return (
    <div aria-busy="true" className="flex flex-col gap-2">
      <Skeleton className="h-9 w-full" />
      {Array.from({ length: linhas }, (_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  )
}

export function EsqueletoDaFicha() {
  return (
    <div data-slot="esqueleto-da-ficha" aria-busy="true" className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 rounded-md border bg-card p-6">
        <Skeleton className="h-3 w-56" />
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="grid grid-cols-2 gap-4 pt-2 md:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3 rounded-md border bg-card p-3">
        <Skeleton className="h-9 w-2/3" />
        <EsqueletoDeTabela />
      </div>
    </div>
  )
}

export function EstadoDoPedido<T>({
  pedido,
  oQue,
  esqueleto,
  children,
}: {
  pedido: Pedido<T>
  oQue: string
  esqueleto?: ReactNode
  children: (dados: T) => ReactNode
}) {
  if (pedido.erro !== null) {
    return <FalhaNaSecao mensagem={`Não foi possível carregar ${oQue}: ${pedido.erro}`} aoTentar={pedido.recarregar} />
  }
  if (pedido.dados === null) return <>{esqueleto ?? <EsqueletoDeTabela />}</>
  return <>{children(pedido.dados)}</>
}
